/**
 * Frontend feature flags.
 *
 * These are env-driven and read at build time by Vite. To flip in production,
 * set the corresponding env var in Vercel and redeploy.
 */

/**
 * Whether to expose Ethereum mainnet portfolios in the UI.
 *
 * Default: `false`. When off, `ChoosePortfolio` filters out portfolios with
 * `chain_id !== 8453` (Base) so production stays single-chain even if mainnet
 * rows exist in `token_indexes`. Flip to `true` in Vercel env when the backend
 * has deployed a mainnet vault and seeded the row.
 */
export const FEATURE_ETH_CHAIN_ENABLED =
  import.meta.env.VITE_ENABLE_ETH_CHAIN === 'true';

/**
 * Whether the "You receive" preview uses the real /folio/mint quote
 * (`estimatedSharesFormatted`, which accounts for swap slippage + fees) instead
 * of the naive NAV-per-share estimate that overstates the amount.
 *
 * Default: `true`. Kill-switch — set `VITE_MINT_QUOTE_PREVIEW=false` in Vercel
 * and redeploy to instantly revert to the old NAV-based estimate if the live
 * quote causes problems. Even when on, the preview falls back to the NAV
 * estimate while the quote loads or if it errors.
 */
export const FEATURE_MINT_QUOTE_PREVIEW_ENABLED =
  import.meta.env.VITE_MINT_QUOTE_PREVIEW !== 'false';

/**
 * Whether to use the EIP-5792 batched (1-popup) approve+mint flow.
 *
 * Default: `false`. When on, only wallets that report BOTH `atomicBatch` and
 * `gasLimitOverride` capabilities on the portfolio chain use it — today that is
 * the Base Account/Coinbase Smart Wallet, whose near-minimum UserOperation call
 * gas limit reverted heavy zapMint batches on-chain (production 04/09 and
 * 01/10; jessepollak/home#495). For those wallets the mint leg carries an
 * app-provided gas budget (`capabilities.gasLimitOverride`) and the dispatch
 * goes straight to the connector provider (viem's `sendCalls` drops per-call
 * capabilities). Every other wallet stays on the reliable standard 2-step flow.
 */
export const FEATURE_BATCHED_SWAP_ENABLED =
  import.meta.env.VITE_ENABLE_BATCHED_SWAP === 'true';

/**
 * Whether to inject a fake mainnet portfolio in dev mode for QA.
 *
 * Only active in `import.meta.env.DEV`. Lets QA validate the full UX of a
 * mainnet portfolio (badge, min, gas warning, switch-chain prompt) without
 * needing the backend to deploy a real vault on mainnet.
 */
export const MOCK_ETH_PORTFOLIO_ENABLED =
  import.meta.env.DEV &&
  import.meta.env.VITE_MOCK_ETH_PORTFOLIO === 'true';

export const MOCK_PORTFOLIO_HOLDINGS_ENABLED =
  import.meta.env.DEV &&
  import.meta.env.VITE_MOCK_PORTFOLIO_HOLDINGS === 'true';

export const MOCK_PDF_REPORT_ENABLED =
  import.meta.env.DEV &&
  import.meta.env.VITE_MOCK_PDF_REPORT !== 'false';

/**
 * Dev-only override that points the QINDEX portfolio at the Reserve Folio
 * contracts (folio share token + zapper + adapter) WITHOUT mutating the shared
 * `token_indexes` row that production reads.
 *
 * Why this exists: prod and dev share one Supabase project, and the QINDEX row
 * still carries the legacy vault address (`0x3Ed3…`) because prod's deployed
 * frontend speaks the old QIndexVault protocol. Re-pointing that row IS the
 * production cutover (see .docs/runbooks/cutover-folio.md). This flag lets us
 * run the full Folio mint/redeem flow against the new contracts on localhost —
 * with REAL on-chain txs on Base — while leaving the DB row (and prod) untouched.
 *
 * Only active in `import.meta.env.DEV`. When on, `usePortfolioFlow` swaps in the
 * folio/zapper/adapter for the matching QINDEX portfolio. Values are configurable
 * via env so we can point at a different folio/chain later without code changes.
 *
 * Activity (user_transactions) from these test txs still writes to the shared DB,
 * keyed by wallet_address — acceptable for test wallets.
 */
export const FOLIO_DEV_OVERRIDE_ENABLED =
  import.meta.env.DEV &&
  import.meta.env.VITE_FOLIO_DEV_OVERRIDE === 'true';

/**
 * The folio override values (Base QINDEX). Env-overridable; these defaults match
 * the live Base deployment documented in the Folio migration runbook.
 */
export const FOLIO_DEV_OVERRIDE = {
  /** on_chain_symbol of the portfolio to override (which token_indexes row to patch). */
  symbol: (import.meta.env.VITE_FOLIO_DEV_SYMBOL as string | undefined) ?? 'QINDEX',
  chainId: Number(import.meta.env.VITE_FOLIO_DEV_CHAIN_ID ?? 8453),
  /** Folio share-token address — becomes the portfolio's vaultAddress. */
  folioAddress: (import.meta.env.VITE_FOLIO_DEV_FOLIO as string | undefined)
    ?? '0x76B33c59E81A0C3d13597F6971147E58c0d46797',
  zapperAddress: (import.meta.env.VITE_FOLIO_DEV_ZAPPER as string | undefined)
    ?? '0xee2f61eAFa7a5e5c27Cbda96289A475aEC7A1217',
  adapterAddress: (import.meta.env.VITE_FOLIO_DEV_ADAPTER as string | undefined)
    ?? '0x067dEBbFB3250e627D42C18f819F03CAAf0f9eC7',
} as const;
